import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
// TODO: Decommission sandbox project (maltrtjtputdznaloovz) once consolidated migration is validated in production.
import { useCustomers } from "@/hooks/useProjects";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarIcon, ArrowRight, ArrowLeft, Plus, ClipboardList, FileText, PlusCircle, ShoppingCart, Trash2, Check, Loader2, Upload, Sparkles, MapPin } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";

type ProjectOrigin = "estimate" | "work_order" | "manual" | "sales_order";

interface SovDraft {
  description: string;
  line_type: string;
  quantity: string;
  unit: string;
  labor_unit_price: string;
  material_unit_price: string;
}

interface ExtractedLineItem {
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  total: number;
  category: "labor" | "material" | "both";
  selected: boolean;
  mappedProductId?: string;
  mappedCostCodeId?: string;
}

interface ExtractedData {
  vendor_name?: string;
  work_order_number?: string;
  line_items: ExtractedLineItem[];
  document_total?: number;
}

const emptySovLine = (): SovDraft => ({
  description: "",
  line_type: "labor",
  quantity: "1",
  unit: "SF",
  labor_unit_price: "0",
  material_unit_price: "0",
});

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const UNITS = ["SF", "LF", "EA", "LS"];

export interface WizardEstimate {
  id: string;
  doc_number: string | null;
  customer_qb_id: string | null;
  customer_ref_name: string | null;
  total_amount: number | null;
  line_items: unknown[] | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialEstimate?: WizardEstimate | null;
  defaultCustomerId?: string;
}

export const NewProjectWizard = ({ open, onOpenChange, initialEstimate, defaultCustomerId }: Props) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  // Step 1
  const [customerId, setCustomerId] = useState(defaultCustomerId || "");
  const [origin, setOrigin] = useState<ProjectOrigin>("manual");
  const [selectedEstimateId, setSelectedEstimateId] = useState<string | null>(null);
  const { data: customers } = useCustomers();

  // Work Order Upload
  const [woFile, setWoFile] = useState<File | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 2 (project details)
  const [typeId, setTypeId] = useState("");
  const [projectName, setProjectName] = useState("");
  const [customerPo, setCustomerPo] = useState("");
  const [subCustomer, setSubCustomer] = useState("");
  const [startDate, setStartDate] = useState<Date>();
  const [status, setStatus] = useState("active");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [pocName, setPocName] = useState("");
  const [pocPhone, setPocPhone] = useState("");
  const [pocEmail, setPocEmail] = useState("");
  const [description, setDescription] = useState("");
  const [hasUnitSchedule, setHasUnitSchedule] = useState(false);
  const [billingCycleType, setBillingCycleType] = useState("monthly");
  const [billingCutoffDay, setBillingCutoffDay] = useState("25");
  const [billingCutoffDay2, setBillingCutoffDay2] = useState("15");
  const [billingType, setBillingType] = useState<string>("unit_based");

  // New type inline
  const [showNewType, setShowNewType] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeAbbr, setNewTypeAbbr] = useState("");

  // SOV lines (final step)
  const [sovLines, setSovLines] = useState<SovDraft[]>([]);

  // Fetch project types
  const { data: projectTypes, refetch: refetchTypes } = useQuery({
    queryKey: ["project_types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_types")
        .select("id, name, abbreviation")
        .order("name");
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Fetch products/services for mapping
  const { data: products } = useQuery({
    queryKey: ["products_services_active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products_services")
        .select("id, code, name, category, unit, default_cost_price, default_markup_pct")
        .eq("status", "active")
        .order("code");
      if (error) throw error;
      return data;
    },
    enabled: !!user && origin === "work_order",
  });

  // Fetch cost codes for mapping
  const { data: costCodes } = useQuery({
    queryKey: ["cost_codes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cost_codes")
        .select("id, code, description")
        .order("code");
      if (error) throw error;
      return data;
    },
    enabled: !!user && origin === "work_order",
  });

  // Fetch QB estimates for selected customer
  const selectedCustomer = customers?.find((c) => c.id === customerId);
  const { data: estimates, isLoading: estimatesLoading } = useQuery({
    queryKey: ["qb_estimates_for_customer", selectedCustomer?.name],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("qb_estimates")
        .select("*")
        .ilike("customer_ref_name", `%${selectedCustomer!.name}%`)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!selectedCustomer?.name,
  });

  // Auto-suggest project name
  useEffect(() => {
    if (!projectName || projectName === autoName) {
      const cName = selectedCustomer?.name || "";
      const tName = projectTypes?.find((t) => t.id === typeId)?.name || "";
      const loc = [address, city, state].filter(Boolean).join(", ");
      const parts = [cName, tName, loc].filter(Boolean);
      setProjectName(parts.join(" - "));
    }
  }, [customerId, typeId, address, city, state]);

  const autoName = useMemo(() => {
    const cName = selectedCustomer?.name || "";
    const tName = projectTypes?.find((t) => t.id === typeId)?.name || "";
    const loc = [address, city, state].filter(Boolean).join(", ");
    return [cName, tName, loc].filter(Boolean).join(" - ");
  }, [selectedCustomer, projectTypes, typeId, address, city, state]);

  // Step calculation: work_order gets an extra "Upload & Map" step after step 1
  const isWO = origin === "work_order";
  const hasSOV = origin === "estimate" || origin === "work_order";
  const totalSteps = isWO ? 4 : hasSOV ? 3 : 2;

  // Logical step mapping:
  // work_order: 1=Customer, 2=Upload&Map, 3=Details, 4=SOV
  // estimate:   1=Customer, 2=Details, 3=SOV
  // manual:     1=Customer, 2=Details

  const canNext1 = !!customerId && !!origin;
  const canNext2WO = extractedData && extractedData.line_items.some((l) => l.selected);
  const detailsStep = isWO ? 3 : 2;
  const sovStep = isWO ? 4 : 3;
  const canNextDetails = !!typeId && !!projectName.trim() && !!address.trim();

  const reset = () => {
    setStep(1);
    setCustomerId(defaultCustomerId || "");
    setOrigin("manual");
    setSelectedEstimateId(null);
    setTypeId("");
    setProjectName("");
    setCustomerPo("");
    setSubCustomer("");
    setStartDate(undefined);
    setStatus("active");
    setAddress("");
    setCity("");
    setState("");
    setZip("");
    setPocName("");
    setPocPhone("");
    setPocEmail("");
    setDescription("");
    setSovLines([]);
    setShowNewType(false);
    setBillingType("unit_based");
    setWoFile(null);
    setExtractedData(null);
    setExtracting(false);
  };

  // Pre-fill from initialEstimate
  useEffect(() => {
    if (open && initialEstimate) {
      reset();
      setOrigin("estimate");
      setSelectedEstimateId(initialEstimate.id);
      const custName = initialEstimate.customer_ref_name || initialEstimate.customer_qb_id;
      if (custName && customers?.length) {
        const match = customers.find((c) => c.name.toLowerCase() === custName.toLowerCase());
        if (match) setCustomerId(match.id);
      }
      if (Array.isArray(initialEstimate.line_items) && initialEstimate.line_items.length > 0) {
        setSovLines(
          (initialEstimate.line_items as any[]).map((li) => {
            if (Array.isArray(li.sub_lines) && li.sub_lines.length > 0) {
              const qty = li.qty || 1;
              const laborBilled = li.labor_total_billed ?? li.labor_total ?? li.sub_lines.filter((s: any) => s.type === "labor").reduce((a: number, s: any) => a + (s.total_billed || s.total || 0), 0);
              const materialBilled = li.material_total_billed ?? li.material_total ?? li.sub_lines.filter((s: any) => s.type === "material").reduce((a: number, s: any) => a + (s.total_billed || s.total || 0), 0);
              const hasLabor = li.sub_lines.some((s: any) => s.type === "labor");
              const hasMaterial = li.sub_lines.some((s: any) => s.type === "material");
              return {
                description: li.description || "Line item",
                line_type: hasLabor && hasMaterial ? "both" : hasLabor ? "labor" : "materials",
                quantity: String(qty),
                unit: li.unit || "LS",
                labor_unit_price: String(qty > 0 ? laborBilled / qty : 0),
                material_unit_price: String(qty > 0 ? materialBilled / qty : 0),
              };
            }
            return {
              description: li.Description || li.description || "Line item",
              line_type: "both",
              quantity: String(li.Qty ?? li.quantity ?? 1),
              unit: li.UnitOfMeasure || li.unit || "LS",
              labor_unit_price: String(li.UnitPrice ?? li.unit_price ?? li.Amount ?? 0),
              material_unit_price: "0",
            };
          })
        );
      } else {
        setSovLines([
          {
            description: `Estimate ${initialEstimate.doc_number || initialEstimate.id.slice(0, 8)}`,
            line_type: "both",
            quantity: "1",
            unit: "LS",
            labor_unit_price: String(initialEstimate.total_amount || 0),
            material_unit_price: "0",
          },
        ]);
      }
      setTimeout(() => {
        if (customerId) setStep(2);
      }, 100);
    }
  }, [open, initialEstimate, customers]);

  const handleSelectEstimate = (est: any) => {
    setSelectedEstimateId(est.id);
    setOrigin("estimate");
    setSovLines([
      {
        description: `Estimate ${est.id?.slice(0, 8)} — ${est.customer_ref_name || ""}`,
        line_type: "both",
        quantity: "1",
        unit: "LS",
        labor_unit_price: String(est.total_amount || 0),
        material_unit_price: "0",
      },
    ]);
  };

  const handleAddType = async () => {
    if (!newTypeName.trim() || !newTypeAbbr.trim()) {
      toast.error("Type name and abbreviation required");
      return;
    }
    const { data: companyId } = await supabase.rpc("my_company_id");
    const { data, error } = await supabase
      .from("project_types")
      .insert({ company_id: companyId!, name: newTypeName.trim(), abbreviation: newTypeAbbr.trim().toUpperCase() })
      .select("id")
      .single();
    if (error) {
      toast.error(error.message);
      return;
    }
    await refetchTypes();
    setTypeId(data.id);
    setShowNewType(false);
    setNewTypeName("");
    setNewTypeAbbr("");
    toast.success("Project type added");
  };

  // --- Work Order Upload & Extract ---
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File must be under 20MB");
      return;
    }
    setWoFile(file);
    setExtractedData(null);
  };

  const handleExtract = async () => {
    if (!woFile) return;
    setExtracting(true);
    try {
      // Upload to storage
      const filePath = `${user!.id}/${Date.now()}_${woFile.name}`;
      const { error: uploadErr } = await supabase.storage
        .from("work-order-docs")
        .upload(filePath, woFile);
      if (uploadErr) throw uploadErr;

      // Call edge function
      const { data, error } = await supabase.functions.invoke("extract-work-order", {
        body: { file_path: filePath },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Extraction failed");

      const items: ExtractedLineItem[] = (data.data.line_items || []).map((li: any) => ({
        ...li,
        unit: li.unit || "LS",
        selected: true,
      }));

      setExtractedData({
        vendor_name: data.data.vendor_name,
        work_order_number: data.data.work_order_number,
        line_items: items,
        document_total: data.data.document_total,
      });

      // Pre-fill WO number
      if (data.data.work_order_number) {
        setCustomerPo(data.data.work_order_number);
      }

      toast.success(`Extracted ${items.length} line items`);
    } catch (e: any) {
      console.error("Extract error:", e);
      toast.error(e.message || "Failed to extract line items");
    } finally {
      setExtracting(false);
    }
  };

  const toggleExtractedItem = (idx: number) => {
    setExtractedData((prev) => {
      if (!prev) return prev;
      const items = [...prev.line_items];
      items[idx] = { ...items[idx], selected: !items[idx].selected };
      return { ...prev, line_items: items };
    });
  };

  const updateExtractedMapping = (idx: number, field: "mappedProductId" | "mappedCostCodeId", value: string) => {
    setExtractedData((prev) => {
      if (!prev) return prev;
      const items = [...prev.line_items];
      items[idx] = { ...items[idx], [field]: value || undefined };
      return { ...prev, line_items: items };
    });
  };

  // Convert extracted items to SOV lines when moving to SOV step
  const convertExtractedToSOV = () => {
    if (!extractedData) return;
    const selected = extractedData.line_items.filter((l) => l.selected);
    const newSovLines: SovDraft[] = selected.map((item) => {
      // If mapped to a product, use product's pricing info
      const product = products?.find((p) => p.id === item.mappedProductId);
      const costPrice = product?.default_cost_price ?? item.unit_price;
      const markup = product?.default_markup_pct ?? 0;
      const billedPrice = costPrice * (1 + markup / 100);
      const laborPrice = item.category === "material" ? 0 : (item.category === "labor" ? billedPrice : costPrice);
      const materialPrice = item.category === "labor" ? 0 : (item.category === "material" ? billedPrice : (item.category === "both" ? item.unit_price - costPrice : 0));

      return {
        description: item.description,
        line_type: item.category === "both" ? "both" : item.category === "labor" ? "labor" : "materials",
        quantity: String(item.quantity),
        unit: item.unit || "LS",
        labor_unit_price: String(laborPrice),
        material_unit_price: String(materialPrice),
      };
    });
    setSovLines(newSovLines);
  };

  const updateSovLine = (idx: number, field: keyof SovDraft, val: string) => {
    setSovLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: val } : l)));
  };

  const removeSovLine = (idx: number) => setSovLines((prev) => prev.filter((_, i) => i !== idx));

  const sovTotals = useMemo(() => {
    let laborTotal = 0;
    let materialTotal = 0;
    sovLines.forEach((l) => {
      const qty = parseFloat(l.quantity) || 0;
      laborTotal += qty * (parseFloat(l.labor_unit_price) || 0);
      materialTotal += qty * (parseFloat(l.material_unit_price) || 0);
    });
    return { laborTotal, materialTotal, total: laborTotal + materialTotal };
  }, [sovLines]);

  const handleCreate = async () => {
    setBusy(true);
    try {
      const { data: companyId, error: cidErr } = await supabase.rpc("my_company_id");
      if (cidErr) throw cidErr;

      const { data: project, error: projErr } = await supabase
        .from("projects")
        .insert({
          name: projectName.trim(),
          company_id: companyId!,
          customer_id: customerId,
          type_id: typeId || null,
          description: description.trim() || null,
          status: status as any,
          address: address.trim() || null,
          city: city.trim() || null,
          state: state.trim() || null,
          zip: zip.trim() || null,
          contracted_by: selectedCustomer?.name || null,
          customer_po_number: customerPo.trim() || null,
          start_date: startDate ? format(startDate, "yyyy-MM-dd") : null,
          project_origin: origin,
          has_unit_schedule: hasUnitSchedule,
          billing_cycle_type: hasUnitSchedule ? billingCycleType : null,
          billing_cutoff_day: hasUnitSchedule ? parseInt(billingCutoffDay) || null : null,
          billing_cutoff_day2: hasUnitSchedule && billingCycleType === "bimonthly" ? parseInt(billingCutoffDay2) || null : null,
          billing_type: billingType,
        } as any)
        .select("id")
        .single();
      if (projErr) throw projErr;

      const { error: updateErr } = await supabase
        .from("projects")
        .update({
          sub_customer: subCustomer.trim() || null,
          point_of_contact_name: pocName.trim() || null,
          point_of_contact_phone: pocPhone.trim() || null,
          point_of_contact_email: pocEmail.trim() || null,
        } as any)
        .eq("id", project.id);
      if (updateErr) console.warn("Extra fields update:", updateErr.message);

      // Create contract + SOV lines if applicable
      if (sovLines.length > 0 && hasSOV) {
        const totalValue = sovTotals.total;
        const { data: contract, error: cErr } = await supabase
          .from("contracts")
          .insert({
            company_id: companyId!,
            project_id: project.id,
            name: `${projectName.trim()} Contract`,
            original_value: totalValue,
            revised_value: totalValue,
            status: "draft",
            created_by: user?.id || null,
          })
          .select("id")
          .single();
        if (cErr) throw cErr;

        const estimateLineItems = (initialEstimate?.line_items as any[]) ?? [];

        const linesToInsert = sovLines.map((l, idx) => {
          const qty = parseFloat(l.quantity) || 0;
          const lup = parseFloat(l.labor_unit_price) || 0;
          const mup = parseFloat(l.material_unit_price) || 0;
          const totalUp = lup + mup;

          const matchedEstLine = estimateLineItems[idx];
          const subLinesJson = matchedEstLine?.sub_lines ?? [];
          const totalCost = matchedEstLine?.total_cost ?? 0;
          const totalBilled = matchedEstLine?.total_billed ?? (qty * totalUp);

          // Extract sub_unit_price from estimate labor cost_price
          let subUnitPrice = 0;
          if (Array.isArray(subLinesJson) && subLinesJson.length > 0) {
            const laborSubs = subLinesJson.filter((s: any) => s.type === "labor");
            if (laborSubs.length > 0) {
              subUnitPrice = laborSubs.reduce((sum: number, s: any) => sum + (Number(s.cost_price) || 0), 0);
            }
          }

          // If from work order, carry cost code mapping
          const extractedItem = isWO ? extractedData?.line_items.filter((l) => l.selected)?.[idx] : null;

          return {
            company_id: companyId!,
            contract_id: contract.id,
            line_number: idx + 1,
            description: l.description,
            line_type: l.line_type,
            quantity: qty,
            unit: l.unit,
            labor_unit_price: lup,
            material_unit_price: mup,
            unit_price: totalUp,
            scheduled_value: totalBilled,
            sub_lines: subLinesJson,
            total_cost: totalCost,
            total_billed: totalBilled,
            cost_code_id: extractedItem?.mappedCostCodeId || null,
            sub_unit_price: subUnitPrice,
          };
        });

        const { error: sovErr } = await supabase.from("sov_lines").insert(linesToInsert as any);
        if (sovErr) throw sovErr;
      }

      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["dashboard_metrics"] });
      toast.success("Project created");
      reset();
      onOpenChange(false);
      navigate(`/controller/projects/${project.id}`);
    } catch (e: any) {
      toast.error(e.message || "Failed to create project");
    } finally {
      setBusy(false);
    }
  };

  // Navigation helpers
  const handleNext = () => {
    if (isWO && step === 2) {
      // Moving from Upload&Map to Details — convert extracted items to SOV
      convertExtractedToSOV();
    }
    setStep(step + 1);
  };

  const canAdvance = () => {
    if (step === 1) return canNext1;
    if (isWO && step === 2) return !!canNext2WO;
    if (step === detailsStep) return canNextDetails;
    return true;
  };

  const isLastStep = step === totalSteps;

  const stepLabel = () => {
    if (step === 1) return "Select a customer and project origin";
    if (isWO && step === 2) return "Upload work order document and map line items";
    if (step === detailsStep) return "Enter project details and location";
    if (step === sovStep) return "Review and edit contract SOV lines";
    return "";
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Project — Step {step} of {totalSteps}</DialogTitle>
          <DialogDescription>{stepLabel()}</DialogDescription>
        </DialogHeader>

        {/* Progress bar */}
        <div className="flex gap-1 mb-2">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={cn(
                "h-1.5 flex-1 rounded-full transition-colors",
                i < step ? "bg-primary" : "bg-muted"
              )}
            />
          ))}
        </div>

        {/* STEP 1 — Customer & Origin */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Customer *</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers?.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {customerId && (
              <div className="space-y-2">
                <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                  Existing Estimates for {selectedCustomer?.name}
                </Label>
                {estimatesLoading ? (
                  <p className="text-sm text-muted-foreground">Loading estimates…</p>
                ) : !estimates?.length ? (
                  <p className="text-sm text-muted-foreground">No QB estimates found for this customer.</p>
                ) : (
                  <div className="grid gap-2 max-h-48 overflow-y-auto">
                    {estimates.map((est: any) => (
                      <Card
                        key={est.id}
                        className={cn(
                          "cursor-pointer transition-colors border",
                          selectedEstimateId === est.id
                            ? "border-primary bg-primary/5"
                            : "hover:border-primary/50"
                        )}
                        onClick={() => handleSelectEstimate(est)}
                      >
                        <CardContent className="p-3 flex items-center justify-between">
                          <div className="flex gap-4 text-sm">
                            <span className="font-mono font-medium">#{est.id?.slice(0, 8)}</span>
                            <span>{est.created_at ? format(new Date(est.created_at), "MMM d, yyyy") : "—"}</span>
                            <span className="font-semibold">{fmt(est.total_amount || 0)}</span>
                            <Badge variant="outline" className="text-xs">{est.txn_status || "—"}</Badge>
                          </div>
                          {selectedEstimateId === est.id && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Separator />

            <div className="space-y-2">
              <Label>Project Origin *</Label>
              <RadioGroup value={origin} onValueChange={(v) => setOrigin(v as ProjectOrigin)} className="grid grid-cols-2 gap-2">
                {[
                  { value: "estimate", label: "From Estimate (QB)", icon: ClipboardList },
                  { value: "work_order", label: "From Work Order", icon: FileText },
                  { value: "manual", label: "New / Manual", icon: PlusCircle },
                  { value: "sales_order", label: "Sales Order", icon: ShoppingCart },
                ].map((opt) => (
                  <Label
                    key={opt.value}
                    className={cn(
                      "flex items-center gap-2 p-3 rounded-md border cursor-pointer transition-colors",
                      origin === opt.value ? "border-primary bg-primary/5" : "hover:border-primary/50"
                    )}
                  >
                    <RadioGroupItem value={opt.value} />
                    <opt.icon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{opt.label}</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>
          </div>
        )}

        {/* STEP 2 (WO only) — Upload & Map */}
        {isWO && step === 2 && (
          <div className="space-y-4">
            {/* Upload Area */}
            <div className="space-y-2">
              <Label>Upload Customer Work Order</Label>
              <div
                className={cn(
                  "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                  woFile ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
                )}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                {woFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    <span className="text-sm font-medium">{woFile.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {(woFile.size / 1024).toFixed(0)} KB
                    </Badge>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Drop a PDF or image of the customer's work order
                    </p>
                    <p className="text-xs text-muted-foreground">PDF, PNG, JPG up to 20MB</p>
                  </div>
                )}
              </div>
            </div>

            {woFile && !extractedData && (
              <Button onClick={handleExtract} disabled={extracting} className="w-full">
                {extracting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Extracting line items…</>
                ) : (
                  <><Sparkles className="mr-2 h-4 w-4" /> Extract Line Items with AI</>
                )}
              </Button>
            )}

            {/* Extracted Results */}
            {extractedData && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium">Extracted Line Items</p>
                    {extractedData.vendor_name && (
                      <p className="text-xs text-muted-foreground">Vendor: {extractedData.vendor_name}</p>
                    )}
                    {extractedData.work_order_number && (
                      <p className="text-xs text-muted-foreground">WO #: {extractedData.work_order_number}</p>
                    )}
                  </div>
                  {extractedData.document_total != null && (
                    <Badge variant="outline" className="font-mono">
                      Doc Total: {fmt(extractedData.document_total)}
                    </Badge>
                  )}
                </div>

                <div className="border rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8">✓</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-14">Qty</TableHead>
                        <TableHead className="w-14">Unit</TableHead>
                        <TableHead className="w-20">$/Unit</TableHead>
                        <TableHead className="w-20">Total</TableHead>
                        <TableHead className="w-16">Type</TableHead>
                        <TableHead className="w-36">Map to Product</TableHead>
                        <TableHead className="w-32">Cost Code</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {extractedData.line_items.map((item, idx) => (
                        <TableRow key={idx} className={cn(!item.selected && "opacity-40")}>
                          <TableCell>
                            <Checkbox
                              checked={item.selected}
                              onCheckedChange={() => toggleExtractedItem(idx)}
                            />
                          </TableCell>
                          <TableCell className="text-xs">{item.description}</TableCell>
                          <TableCell className="text-xs font-mono">{item.quantity}</TableCell>
                          <TableCell className="text-xs">{item.unit}</TableCell>
                          <TableCell className="text-xs font-mono">{fmt(item.unit_price)}</TableCell>
                          <TableCell className="text-xs font-mono font-semibold">{fmt(item.total)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs capitalize">
                              {item.category}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={item.mappedProductId || ""}
                              onValueChange={(v) => updateExtractedMapping(idx, "mappedProductId", v)}
                            >
                              <SelectTrigger className="h-7 text-xs">
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="">None</SelectItem>
                                {products?.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    <span className="flex items-center gap-1">
                                      <span className="font-mono text-muted-foreground">{p.code}</span>
                                      {p.name}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={item.mappedCostCodeId || ""}
                              onValueChange={(v) => updateExtractedMapping(idx, "mappedCostCodeId", v)}
                            >
                              <SelectTrigger className="h-7 text-xs">
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="">None</SelectItem>
                                {costCodes?.map((cc) => (
                                  <SelectItem key={cc.id} value={cc.id}>
                                    <span className="font-mono">{cc.code}</span> {cc.description}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {extractedData.line_items.filter((l) => l.selected).length} of {extractedData.line_items.length} items selected
                  </span>
                  <span className="font-semibold">
                    Selected Total: {fmt(extractedData.line_items.filter((l) => l.selected).reduce((s, l) => s + l.total, 0))}
                  </span>
                </div>

                {/* Re-extract button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setExtractedData(null); setWoFile(null); }}
                >
                  Upload Different Document
                </Button>
              </div>
            )}
          </div>
        )}

        {/* DETAILS STEP */}
        {step === detailsStep && (
          <div className="space-y-4">
            {/* Project Type */}
            <div className="space-y-2">
              <Label>Project Type *</Label>
              <div className="flex gap-2">
                <Select value={typeId} onValueChange={setTypeId}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {projectTypes?.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono text-xs">{t.abbreviation}</Badge>
                          {t.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={() => setShowNewType(!showNewType)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {showNewType && (
                <div className="flex gap-2 items-end p-3 border rounded-md bg-muted/30">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Type Name</Label>
                    <Input value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} placeholder="e.g. Flooring" />
                  </div>
                  <div className="w-24 space-y-1">
                    <Label className="text-xs">Abbr</Label>
                    <Input value={newTypeAbbr} onChange={(e) => setNewTypeAbbr(e.target.value)} placeholder="FL" maxLength={5} />
                  </div>
                  <Button size="sm" onClick={handleAddType}>Add</Button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Project Name *</Label>
              <Input value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="Auto-generated or custom" />
              {autoName && projectName !== autoName && (
                <button className="text-xs text-muted-foreground underline" onClick={() => setProjectName(autoName)}>
                  Reset to: {autoName}
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer PO / Ref #</Label>
                <Input value={customerPo} onChange={(e) => setCustomerPo(e.target.value)} placeholder="e.g. 20966" />
              </div>
              <div className="space-y-2">
                <Label>Sub-Customer</Label>
                <Input value={subCustomer} onChange={(e) => setSubCustomer(e.target.value)} placeholder="Optional end client" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !startDate && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={startDate} onSelect={setStartDate} className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Job Location</p>

            <div className="space-y-2">
              <Label>Address *</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street address" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>City</Label>
                <Input value={city} onChange={(e) => setCity(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input value={state} onChange={(e) => setState(e.target.value)} maxLength={2} />
              </div>
              <div className="space-y-2">
                <Label>Zip</Label>
                <Input value={zip} onChange={(e) => setZip(e.target.value)} maxLength={10} />
              </div>
            </div>

            <Separator />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Point of Contact</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={pocName} onChange={(e) => setPocName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={pocPhone} onChange={(e) => setPocPhone(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={pocEmail} onChange={(e) => setPocEmail(e.target.value)} type="email" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Job Description</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" rows={3} />
            </div>

            <Separator />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Project Type</p>
            <RadioGroup value={billingType} onValueChange={(v) => {
              setBillingType(v);
              if (v === "unit_based") setHasUnitSchedule(true);
              else if (v === "sov_only") setHasUnitSchedule(false);
            }} className="space-y-2">
              <Label className={cn("flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors", billingType === "unit_based" ? "border-primary bg-primary/5" : "hover:border-primary/50")}>
                <RadioGroupItem value="unit_based" className="mt-0.5" />
                <div>
                  <span className="text-sm font-medium">Unit-based</span>
                  <p className="text-xs text-muted-foreground">Residential, multifamily, hotels — track completion per unit</p>
                </div>
              </Label>
              <Label className={cn("flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors", billingType === "sov_only" ? "border-primary bg-primary/5" : "hover:border-primary/50")}>
                <RadioGroupItem value="sov_only" className="mt-0.5" />
                <div>
                  <span className="text-sm font-medium">SOV only</span>
                  <p className="text-xs text-muted-foreground">Commercial, lump sum, service — track completion by % or quantity</p>
                </div>
              </Label>
              <Label className={cn("flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors", billingType === "mixed" ? "border-primary bg-primary/5" : "hover:border-primary/50")}>
                <RadioGroupItem value="mixed" className="mt-0.5" />
                <div>
                  <span className="text-sm font-medium">Mixed</span>
                  <p className="text-xs text-muted-foreground">Combination — configure per SOV line (unit vs quantity vs lump sum)</p>
                </div>
              </Label>
            </RadioGroup>

            {billingType === "unit_based" && (
              <>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-4">Unit Schedule</p>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox checked={hasUnitSchedule} onCheckedChange={(v) => setHasUnitSchedule(!!v)} />
                  This project has a unit schedule (hotels, apartments, multi-unit properties)
            </label>

            {hasUnitSchedule && (
              <div className="space-y-3 pl-6 border-l-2 border-primary/20">
                <div className="space-y-2">
                  <Label>Billing Cycle</Label>
                  <Select value={billingCycleType} onValueChange={setBillingCycleType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="bimonthly">Bi-Monthly</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {billingCycleType === "monthly" && (
                  <div className="space-y-2">
                    <Label>Cutoff day of each month</Label>
                    <Input type="number" min={1} max={31} value={billingCutoffDay} onChange={(e) => setBillingCutoffDay(e.target.value)} className="w-24" />
                  </div>
                )}

                {billingCycleType === "bimonthly" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>First cutoff day</Label>
                      <Input type="number" min={1} max={31} value={billingCutoffDay} onChange={(e) => setBillingCutoffDay(e.target.value)} className="w-24" />
                    </div>
                    <div className="space-y-2">
                      <Label>Second cutoff day</Label>
                      <Input type="number" min={1} max={31} value={billingCutoffDay2} onChange={(e) => setBillingCutoffDay2(e.target.value)} className="w-24" />
                    </div>
                  </div>
                )}

                {billingCycleType === "weekly" && (
                  <div className="space-y-2">
                    <Label>Cutoff day</Label>
                    <Select value={billingCutoffDay} onValueChange={setBillingCutoffDay}>
                      <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d, i) => (
                          <SelectItem key={d} value={String(i + 1)}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  Note: Billing cycle is set by Accounting. Contact accounting before selecting.
                </p>
              </div>
            )}
              </>
            )}
          </div>
        )}

        {/* SOV STEP */}
        {step === sovStep && hasSOV && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Contract SOV Lines</p>
              <Button size="sm" variant="outline" onClick={() => setSovLines((prev) => [...prev, emptySovLine()])}>
                <Plus className="mr-1 h-3 w-3" /> Add Line
              </Button>
            </div>

            {!sovLines.length ? (
              <p className="text-sm text-muted-foreground">No SOV lines. Add lines or go back.</p>
            ) : (
              <div className="border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">#</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-20">Type</TableHead>
                      <TableHead className="w-16">Qty</TableHead>
                      <TableHead className="w-16">Unit</TableHead>
                      <TableHead className="w-24">Labor $</TableHead>
                      <TableHead className="w-24">Material $</TableHead>
                      <TableHead className="w-24">Total $/u</TableHead>
                      <TableHead className="w-24">Line Total</TableHead>
                      <TableHead className="w-8"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sovLines.map((line, idx) => {
                      const qty = parseFloat(line.quantity) || 0;
                      const lup = parseFloat(line.labor_unit_price) || 0;
                      const mup = parseFloat(line.material_unit_price) || 0;
                      const totalUp = lup + mup;
                      const lineTotal = qty * totalUp;
                      const showLabor = line.line_type === "labor" || line.line_type === "both";
                      const showMat = line.line_type === "materials" || line.line_type === "both";

                      return (
                        <TableRow key={idx}>
                          <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                          <TableCell>
                            <Input
                              value={line.description}
                              onChange={(e) => updateSovLine(idx, "description", e.target.value)}
                              className="h-8 text-xs"
                            />
                          </TableCell>
                          <TableCell>
                            <Select value={line.line_type} onValueChange={(v) => {
                              updateSovLine(idx, "line_type", v);
                              if (v === "labor") updateSovLine(idx, "material_unit_price", "0");
                              if (v === "materials") updateSovLine(idx, "labor_unit_price", "0");
                            }}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="labor">Labor</SelectItem>
                                <SelectItem value="materials">Material</SelectItem>
                                <SelectItem value="both">Both</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={line.quantity}
                              onChange={(e) => updateSovLine(idx, "quantity", e.target.value)}
                              className="h-8 text-xs w-16"
                            />
                          </TableCell>
                          <TableCell>
                            <Select value={line.unit} onValueChange={(v) => updateSovLine(idx, "unit", v)}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            {showLabor ? (
                              <Input
                                type="number"
                                step="0.01"
                                value={line.labor_unit_price}
                                onChange={(e) => updateSovLine(idx, "labor_unit_price", e.target.value)}
                                className="h-8 text-xs w-24"
                              />
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {showMat ? (
                              <Input
                                type="number"
                                step="0.01"
                                value={line.material_unit_price}
                                onChange={(e) => updateSovLine(idx, "material_unit_price", e.target.value)}
                                className="h-8 text-xs w-24"
                              />
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground font-mono">{fmt(totalUp)}</TableCell>
                          <TableCell className="text-xs font-mono font-semibold">{fmt(lineTotal)}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeSovLine(idx)}>
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {sovLines.length > 0 && (
              <div className="flex justify-end gap-6 text-sm">
                <span>Labor: <strong>{fmt(sovTotals.laborTotal)}</strong></span>
                <span>Material: <strong>{fmt(sovTotals.materialTotal)}</strong></span>
                <span>Total: <strong className="text-primary">{fmt(sovTotals.total)}</strong></span>
              </div>
            )}
          </div>
        )}

        {/* Footer Navigation */}
        <div className="flex justify-between pt-2">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="mr-1 h-4 w-4" /> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
            {!isLastStep ? (
              <Button onClick={handleNext} disabled={!canAdvance()}>
                Next <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleCreate} disabled={busy || !canNextDetails}>
                {busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Creating…</> : "Create Project"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
